import React, { useEffect, useState } from 'react';
import Modal from './common/Modal';
import { fieldStyles } from '../config/formStyles';
import { theme } from '../config/theme';
import { subscribeProductores, requestProductoresSync } from '../services/productoresService';
import { subscribeComerciales } from '../services/comercialesClientesService';
import { describirBajasOmitidas, resumirBajasOmitidas } from '../utils/bajasOmitidas';
import { isAxonEmail } from '../utils/emailDomain';

// Código con el que el backend avisa que abortó la sincronización porque el SGC
// vino anómalo (ver functions/productoresSync.js). Con la versión vigente de la
// función, una caída anómala de activos ya no aborta: devuelve `bajasOmitidas`.
const SYNC_ANOMALO = 'sync-anomalo';

const CONFIRMAR_BAJAS = '\n\nSi esas bajas son correctas (así figuran en el Sistema de Gestión Comercial), '
    + '¿aplicarlas ahora?';

const styles = {
    intro: { margin: '0 0 1rem 0', fontSize: '0.875rem', color: theme.colors.textSecondary, lineHeight: 1.5 },
    sectionTitle: {
        fontFamily: theme.typography.titleFamily,
        fontSize: '0.8rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.colors.primary,
        margin: '1.5rem 0 0.5rem 0',
    },
    list: {
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
        backgroundColor: theme.colors.surface,
        maxHeight: '32vh',
        overflowY: 'auto',
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '0.75rem 1rem',
        borderBottom: `1px solid ${theme.colors.border}`,
    },
    rowMain: { display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0 },
    rowName: { fontSize: '0.95rem', fontWeight: '600', color: theme.colors.secondary },
    rowMeta: { fontSize: '0.78rem', color: theme.colors.textSecondary },
    rowWarning: { fontSize: '0.78rem', color: '#92400E' },
    badge: (active) => ({
        display: 'inline-block',
        padding: '0.15rem 0.55rem',
        borderRadius: '999px',
        fontSize: '0.7rem',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        backgroundColor: active ? '#D1FAE5' : '#FEE2E2',
        color: active ? '#065F46' : '#991B1B',
    }),
    empty: { padding: '1.25rem 1rem', textAlign: 'center', fontSize: '0.875rem', color: theme.colors.textSecondary },
    feedback: (isError) => ({
        marginTop: '1rem',
        padding: '0.75rem 1rem',
        borderRadius: theme.borderRadius,
        fontSize: '0.85rem',
        backgroundColor: isError ? '#FEE2E2' : '#D1FAE5',
        color: isError ? '#991B1B' : '#065F46',
    }),
    pendingBanner: {
        margin: '0 0 1rem 0',
        padding: '0.75rem 1rem',
        borderRadius: theme.borderRadius,
        fontSize: '0.85rem',
        lineHeight: 1.5,
        backgroundColor: '#FEF3C7',
        color: '#92400E',
    },
};

const formatFecha = (ts) => {
    if (!ts) return 'nunca';
    const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return date.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Panel (solo personal interno) de productores externos: el padrón sincronizado
 * desde el Sistema de Gestión Comercial, que es la única fuente de los clientes
 * de cada productor.
 */
const ProductoresAdminModal = ({ isOpen, onClose, user }) => {
    const [productores, setProductores] = useState([]);
    const [comerciales, setComerciales] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sync, setSync] = useState({ running: false, message: '', isError: false });

    useEffect(() => {
        if (!isOpen) return undefined;
        setIsLoading(true);
        const unsubProductores = subscribeProductores((docs) => {
            setProductores([...docs].sort((a, b) => (a.nombre || a.email).localeCompare(b.nombre || b.email, 'es')));
            setIsLoading(false);
        }, () => setIsLoading(false));
        // Solo para el aviso de bajas pendientes: si falla, el panel sigue igual.
        const unsubComerciales = subscribeComerciales(setComerciales, () => setComerciales([]));
        return () => {
            unsubProductores();
            unsubComerciales();
        };
    }, [isOpen]);

    const bajasPendientes = resumirBajasOmitidas(productores, comerciales);

    const handleSync = async (rebuildSaldos, forzar = false) => {
        setSync({ running: true, message: '', isError: false });
        try {
            const result = await requestProductoresSync(user, { rebuildSaldos, forzar });
            const partes = [
                `${result.productores} productor(es) sincronizado(s)`,
                `${result.clientesSinComitente} cliente(s) sin comitente`,
                `${result.desactivados} desactivado(s)`,
            ];
            if (result.comerciales) {
                partes.push(`${result.comerciales.comerciales} comercial(es) con clientes asignados`
                    + (result.comerciales.comercialesSinCuentas
                        ? ` (${result.comerciales.comercialesSinCuentas} sin cuentas)`
                        : ''));
            }
            if (result.saldos) {
                partes.push(result.saldos.snapshotId
                    ? `saldos por comitente regenerados (${result.saldos.escritos} cuentas)`
                    : 'sin snapshot de hoy para regenerar saldos');
            }
            const resumen = `Sincronización completada: ${partes.join(', ')}.`;
            // Caída anómala de activos: se aplicaron altas y cambios, no las bajas.
            if (result.bajasOmitidas && !forzar) {
                const aviso = `${resumen} ${describirBajasOmitidas(result.bajasOmitidas)}`;
                setSync({ running: false, isError: true, message: aviso });
                if (window.confirm(`${describirBajasOmitidas(result.bajasOmitidas)}${CONFIRMAR_BAJAS}`)) {
                    await handleSync(rebuildSaldos, true);
                }
                return;
            }
            setSync({ running: false, message: resumen, isError: false });
        } catch (error) {
            // El SGC vino con muchas menos personas activas que el padrón actual y la
            // corrida no escribió nada. Si las bajas son legítimas, se confirma y se fuerza.
            if (error.code === SYNC_ANOMALO && !forzar) {
                setSync({ running: false, isError: true, message: error.message });
                const confirmar = window.confirm(
                    `${error.message}\n\nSi esas bajas son correctas (así figuran en el Sistema de Gestión `
                    + 'Comercial), ¿sincronizar igual?',
                );
                if (confirmar) await handleSync(rebuildSaldos, true);
                return;
            }
            setSync({
                running: false,
                isError: true,
                message: error.code === SYNC_ANOMALO
                    ? error.message
                    : 'No se pudo sincronizar. Revisá la consola y que la función tenga acceso al proyecto del SGC.',
            });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            title="Productores externos"
            maxWidth="820px"
            footer={<>
                <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cerrar</button>
                <button
                    type="button"
                    onClick={() => handleSync(true)}
                    disabled={sync.running}
                    style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton, opacity: sync.running ? 0.6 : 1 }}
                    title="Además de sincronizar el padrón, regenera los saldos por comitente que ven los productores a partir del snapshot de hoy"
                >
                    Sincronizar y regenerar saldos
                </button>
                <button
                    type="button"
                    onClick={() => handleSync(false)}
                    disabled={sync.running}
                    style={{ ...fieldStyles.button, opacity: sync.running ? 0.6 : 1 }}
                >
                    {sync.running ? 'Sincronizando...' : 'Sincronizar ahora'}
                </button>
            </>}
        >
            <p style={styles.intro}>
                Los productores se dan de alta en el Sistema de Gestión Comercial (tipo "Productor", con el
                email de la cuenta de Google con la que van a ingresar: la de @axoncapital.com.ar o una
                externa, como Gmail, escrita exactamente igual). Este padrón se sincroniza solo cada hora en
                días hábiles; al loguearse, un productor entra directo a su planilla y solo ve las cuentas
                listadas acá. Una cuenta externa nunca accede como personal interno.
                <br />
                Los clientes de cada productor salen <strong>únicamente</strong> de los que tiene asignados en el
                Sistema de Gestión Comercial: para habilitarle o quitarle una cuenta, se cambia allá.
            </p>

            {bajasPendientes && (
                <div style={styles.pendingBanner} role="status">
                    <strong>Bajas pendientes de confirmar.</strong> {describirBajasOmitidas(bajasPendientes)}
                    {' '}Si son correctas, usá "Sincronizar ahora" y confirmalas.
                </div>
            )}

            <div style={{ ...styles.sectionTitle, marginTop: 0 }}>Padrón de productores</div>
            <div style={styles.list}>
                {isLoading && <div style={styles.empty}>Cargando padrón...</div>}
                {!isLoading && productores.length === 0 && (
                    <div style={styles.empty}>Todavía no hay productores sincronizados. Usá "Sincronizar ahora".</div>
                )}
                {!isLoading && productores.map(p => (
                    <div key={p.id} style={styles.row}>
                        <div style={styles.rowMain}>
                            <span style={styles.rowName}>{p.nombre || p.email}</span>
                            <span style={styles.rowMeta}>
                                {p.email}{!isAxonEmail(p.email) && ' (cuenta externa)'} · {p.comitentes.length} cuenta(s)
                                {' · '}sincronizado {formatFecha(p.syncedAt)}
                            </span>
                            {p.clientesSinComitente.length > 0 && (
                                <span style={styles.rowWarning}>
                                    Sin comitente en el SGC: {p.clientesSinComitente.join(', ')}
                                </span>
                            )}
                            {p.desactivadoMotivo && p.active === false && (
                                <span style={styles.rowWarning}>{p.desactivadoMotivo}</span>
                            )}
                            {p.bajaOmitida && (
                                <span style={styles.rowWarning}>
                                    Baja pendiente de confirmar: el SGC ya no lo trae activo, pero la sincronización
                                    no la aplicó por una caída anómala del padrón.
                                </span>
                            )}
                        </div>
                        <span style={styles.badge(p.active !== false)}>{p.active !== false ? 'Activo' : 'Inactivo'}</span>
                    </div>
                ))}
            </div>

            {sync.message && <div style={styles.feedback(sync.isError)}>{sync.message}</div>}
        </Modal>
    );
};

export default ProductoresAdminModal;
